/**
 * @file
 * Sticky hero CTA. On phones, shows a bar pinned to the bottom of the screen
 * once the registration form has scrolled off the top, and hides it again on
 * the way back up.
 *
 * The bar is markup-hidden until this script says otherwise, and this script
 * only ever unhides it below the phone breakpoint. That is deliberate: the
 * stylesheet is aggregated, so leaving "phones only" to a media query alone
 * would let the bar show at every width whenever the browser is a cache clear
 * behind. _campaign.scss still carries the matching query, but as the thing
 * that governs layout rather than the thing that guards it.
 *
 * "Scrolled off the top" is deliberate rather than plain "not visible": the
 * form is the first thing on the page, so on load it is either on screen or
 * being scrolled through. Toggling on bare non-intersection would flash the bar
 * during the first paint on short viewports.
 */
(function (Drupal, once) {
  'use strict';

  // Foundation's `medium` breakpoint, one step down — the width at which
  // _campaign.scss stops drawing the bar. Keep the two in step.
  var PHONE = '(max-width: 39.99875em)';

  Drupal.behaviors.campaignStickyCta = {
    attach: function attach(context) {
      var bars = once('campaign-sticky-cta', '[data-campaign-sticky]', context);

      if (!bars.length || !('IntersectionObserver' in window)) {
        return;
      }

      bars.forEach(function (bar) {
        // Not the bar itself — it is fixed, so it never leaves the viewport and
        // would report itself visible forever.
        var hero = bar.closest('.campaign-hero');

        if (!hero) {
          return;
        }

        // The form, not the whole hero: the bar offers what the form already
        // shows, so it is wanted the moment the form leaves rather than after
        // the hero's art and its overlap padding have scrolled by too.
        var watched = hero.querySelector('.campaign-hero__form') || hero;

        // The overlap system gives every section wrapper its own stacking
        // context, so a fixed bar left inside the hero paints under each later
        // section no matter how high its z-index. Body is the only ancestor
        // above all of them, and it carries .campaign-page, so the button keeps
        // its styling. Move before observing — closest() is used above.
        document.body.appendChild(bar);

        var phone = window.matchMedia(PHONE);

        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              // bottom <= 0 is the form sitting above the viewport. Below
              // it, the same non-intersecting state means the visitor has not
              // reached the form yet, and the bar stays away.
              var scrolledPast =
                !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;

              bar.classList.toggle('is-visible', scrolledPast);
            });
          },
          { threshold: 0 }
        );

        // Re-runs whenever the viewport crosses the breakpoint, so a rotation
        // or a resized desktop window lands in the right state. observe() and
        // unobserve() are both no-ops when already in that state.
        function sync() {
          if (phone.matches) {
            bar.removeAttribute('hidden');
            observer.observe(watched);
            return;
          }

          observer.unobserve(watched);
          bar.classList.remove('is-visible');
          bar.setAttribute('hidden', '');
        }

        sync();

        if (phone.addEventListener) {
          phone.addEventListener('change', sync);
        } else if (phone.addListener) {
          // Safari below 14 never got addEventListener on MediaQueryList.
          phone.addListener(sync);
        }
      });
    },
  };
})(Drupal, once);
